/** Matches Game API `analytics_config_type_enum`. */
export const ANALYTICS_CONFIG_TYPES = [
  { value: 'AppInsights', text: 'Application Insights' },
  { value: 'GA', text: 'Google Analytics' },
  { value: 'Sentry', text: 'Sentry' },
];

export function analyticsConfigTypeLabel(type) {
  const found = ANALYTICS_CONFIG_TYPES.find((t) => t.value === type);
  return found ? found.text : type || '—';
}

/** Flatten class-validator constraint objects into readable messages. */
function constraintMessages(entry) {
  if (!entry) return [];
  if (typeof entry === 'string') return [entry];
  if (entry.constraints && typeof entry.constraints === 'object') {
    return Object.values(entry.constraints).filter(Boolean).map(String);
  }
  if (Array.isArray(entry.children) && entry.children.length) {
    return entry.children.flatMap(constraintMessages);
  }
  return [];
}

function messagePayloadToText(payload) {
  if (!payload) return null;
  if (typeof payload === 'string') return payload;
  if (Array.isArray(payload)) {
    const parts = payload.flatMap((item) => {
      if (typeof item === 'string') return [item];
      const nested = item?.Message ?? item?.message;
      if (Array.isArray(nested)) return nested.flatMap(constraintMessages);
      if (nested && typeof nested === 'object') return constraintMessages(nested);
      if (nested) return [String(nested)];
      return constraintMessages(item);
    }).filter(Boolean);
    return parts.length ? parts.join('. ') : null;
  }
  if (typeof payload === 'object') {
    const nested = payload.Message ?? payload.message;
    if (nested) return messagePayloadToText(nested);
    const fromConstraints = constraintMessages(payload);
    return fromConstraints.length ? fromConstraints.join('. ') : null;
  }
  return null;
}

function detailMessages(details) {
  return messagePayloadToText(details);
}

/** Parse Game API / axios-style errors into a short user-facing string. */
export function apiErrorMessage(error, fallback = 'Request failed') {
  if (!error) return fallback;
  if (typeof error === 'string') return error;

  // Rejected JSON body may be an array: [{ Message: [...], ErrorCode }]
  if (Array.isArray(error)) {
    return messagePayloadToText(error) || fallback;
  }

  const fromDetails = detailMessages(error.details);
  if (fromDetails) return fromDetails;

  if (typeof error.message === 'string' && error.message && !error.statusCode) {
    return error.message;
  }
  const fromMessage = messagePayloadToText(error.message) || messagePayloadToText(error.Message);
  if (fromMessage) return fromMessage;

  const nested = detailMessages(error.response?.data?.details)
    || messagePayloadToText(error.response?.data)
    || error.response?.data?.error;
  if (nested) return Array.isArray(nested) ? nested.join(', ') : String(nested);

  if (error.error) return String(error.error);
  return fallback;
}
